import { shortDate } from "@/project/lib/format";

export type LetterOrg = {
  id: string;
  name: string;
  short_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  area: string | null;
  city: string | null;
  pincode: string | null;
  visiting_day: string | null;
  visiting_time: string | null;
};

export type LetterApplication = {
  ref_no: string;
  application_date: string;
  applicant_name: string;
  applicant_address: string | null;
  applicant_mobile: string | null;
  applicant_aadhaar: string | null;
  ration_card_no: string | null;
  beneficiary_name: string | null;
  total_requirement: number;
  applicant_contribution: number;
  our_contribution: number;
  other_received: number;
  balance_required: number;
  bank_account_holder: string | null;
  bank_name: string | null;
  bank_account_no: string | null;
  bank_ifsc: string | null;
  categoryName?: string;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

/** Row inside the bordered money grid on the left, exactly like the printed form. */
function MoneyRow({
  label,
  value,
  bold,
}: {
  label: string;
  value?: string;
  bold?: boolean;
}) {
  return (
    <tr>
      <td
        className={`border border-neutral-800 px-1 py-[2px] ${bold ? "font-semibold" : ""}`}
      >
        {label}
      </td>
      <td
        className={`w-[38%] border border-neutral-800 px-1 py-[2px] text-right ${
          bold ? "font-semibold" : ""
        }`}
      >
        {value ?? ""}
      </td>
    </tr>
  );
}

/** Value printed on a ruled line, like the underlined blanks on the original letter. */
function Blank({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-block min-w-[30mm] border-b border-neutral-800 px-1 text-center ${className}`}
    >
      {children}
    </span>
  );
}

/** Plain number with thousand separators — the original form prints amounts without ₹. */
function amt(n: number) {
  return new Intl.NumberFormat("en-IN").format(Math.round(Number(n) || 0));
}

export function LetterSheet({
  app,
  org,
  allOrgs,
  amounts,
  documents,
  settings,
  createdDate,
  generatedDate,
}: {
  app: LetterApplication;
  org: LetterOrg;
  allOrgs: LetterOrg[];
  amounts?: Record<string, string | number>;
  documents: string[];
  settings: Record<string, string>;
  createdDate?: string | null;
  generatedDate?: string | null;
}) {
  const validityDays = Number(settings["letter_validity_days"] || 15);
  const issue = new Date(app.application_date);
  const validUntil = new Date(issue.getTime() + validityDays * 86400000);
  const askedHere = Number(amounts?.[org.id] ?? 0);

  return (
    <div className="letter-sheet mx-auto w-[148mm] max-w-full bg-white p-[4mm] font-serif text-[8.4pt] leading-snug text-neutral-900 shadow-sm print:shadow-none">
      <div className="border border-neutral-800 p-[4mm]">
        {/* Letterhead */}
        <header className="text-center">
          <h2 className="inline-block border-b-2 border-neutral-800 pb-[1px] text-[12.5pt] font-bold leading-tight">
            {settings["org_heading"]}
          </h2>
          <p className="mt-2 text-[9.4pt] font-bold underline">
            Managed by: {settings["managed_by"]}
          </p>
          <p className="mt-1 text-[8pt]">
            Trust Regd No: {settings["trust_regd_no"]} &nbsp; CSR No:{" "}
            {settings["csr_no"]}
          </p>
          <p className="mt-2 text-[8pt]">{settings["office_address"]}</p>
          <p className="text-[8pt]">
            Mobile: {settings["mobile"]} &nbsp; email: {settings["email"]}
          </p>
        </header>

        {/* Date / Ref box */}
        <div className="mt-3 flex justify-end">
          <table className="w-[60mm] border-collapse text-[8.4pt]">
            <tbody>
              <tr>
                <td className="border border-neutral-800 px-2 py-[3px] text-right">
                  Date:
                </td>
                <td className="w-[22mm] border border-neutral-800 px-2 py-[3px] text-center">
                  {shortDate(app.application_date)}
                </td>
              </tr>
              <tr>
                <td className="border border-neutral-800 px-2 py-[3px] text-right">
                  Ref. Letter No:
                </td>
                <td className="border border-neutral-800 px-2 py-[3px] text-center">
                  {app.ref_no}
                </td>
              </tr>
              <tr>
                <td className="border border-neutral-800 px-2 py-[3px] text-right">
                  Letter Created:
                </td>
                <td className="border border-neutral-800 px-2 py-[3px] text-center">
                  {shortDate(createdDate ?? app.application_date)}
                </td>
              </tr>
              <tr>
                <td className="border border-neutral-800 px-2 py-[3px] text-right">
                  Letter Generated:
                </td>
                <td className="border border-neutral-800 px-2 py-[3px] text-center">
                  {shortDate(generatedDate ?? todayIso())}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Addressee + photo box */}
        <div className="mt-3 flex items-start justify-between gap-6">
          <div className="text-[8.4pt]">
            <p>To,</p>
            <p className="mt-1">
              {[org.short_name, org.name].filter(Boolean).join("  ")}
            </p>
            {org.address_line1 ? <p>{org.address_line1}</p> : null}
            {org.address_line2 ? <p>{org.address_line2}</p> : null}
            <p>
              {[org.area, org.city].filter(Boolean).join(", ")}
              {org.pincode ? ` - ${org.pincode}` : ""}
            </p>
            {org.visiting_day || org.visiting_time ? (
              <p>
                {org.visiting_day}
                {org.visiting_day && org.visiting_time ? ", " : ""}
                {org.visiting_time ? `Timing: ${org.visiting_time}` : ""}
              </p>
            ) : null}
          </div>
          <div className="h-[24mm] w-[30mm] shrink-0 border border-neutral-800" />
        </div>

        {/* Applicant block */}
        <p className="mt-3">Respected Director,</p>
        <div className="mt-2 space-y-[6px] text-[8.4pt]">
          <p>
            We humbly recommend for Mr. / Mrs. &nbsp;
            <Blank className="min-w-[30mm]">{app.applicant_name}</Blank>
          </p>
          <p>
            Resident at:{" "}
            <Blank className="min-w-[30mm]">
              {app.applicant_address ?? ""}
            </Blank>
          </p>
          <p className="flex flex-wrap items-end gap-x-3">
            <span>
              Beneficiary Name:{" "}
              <Blank className="min-w-[26mm]">
                {app.beneficiary_name ?? app.applicant_name}
              </Blank>
            </span>
            <span>
              Mobile:{" "}
              <Blank className="min-w-[26mm]">
                {app.applicant_mobile ?? ""}
              </Blank>
            </span>
          </p>
          <p>
            Needs some amount for : &nbsp;
            <Blank className="min-w-[32mm]">{app.categoryName ?? ""}</Blank>
          </p>
        </div>

        {/* Money grid + institutions + documents */}
        <div className="mt-4 grid grid-cols-[36%_32%_32%] gap-3 text-[8.4pt]">
          <table className="w-full border-collapse self-start">
            <tbody>
              <MoneyRow
                label="Total Requirment:"
                value={amt(app.total_requirement)}
              />
              <MoneyRow label="Received from:-" />
              {Number(app.other_received) > 0 ? (
                <MoneyRow
                  label="Other Received"
                  value={amt(app.other_received)}
                />
              ) : (
                <MoneyRow label="" />
              )}
              <MoneyRow label="Own" value={amt(app.applicant_contribution)} />
              <MoneyRow label="" />
              <MoneyRow
                label="Our Contribution"
                value={amt(app.our_contribution)}
              />
              <MoneyRow
                label="Balance Required till today"
                value={amt(app.balance_required)}
                bold
              />
              {askedHere > 0 ? (
                <MoneyRow
                  label="Requested from you"
                  value={amt(askedHere)}
                  bold
                />
              ) : null}
            </tbody>
          </table>

          <div>
            <p className="mb-2 font-semibold underline">
              We are recommending to:
            </p>
            <ul className="space-y-[7px]">
              {allOrgs.map(o => {
                const a = Number(amounts?.[o.id] ?? 0);
                const mine = o.id === org.id;
                return (
                  <li key={o.id} className="flex justify-between gap-2">
                    <span className={mine ? "font-semibold" : ""}>
                      {o.short_name ?? o.name}
                    </span>
                    {a > 0 ? (
                      <span className={mine ? "font-semibold" : ""}>
                        {amt(a)}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="text-[7.2pt]">
            <p className="mb-2 font-semibold underline">
              {settings["documents_note"] ||
                "Take Original documents with xerox"}
            </p>
            <ul className="space-y-[7px]">
              {documents.map(d => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          </div>
        </div>

        <p className="mt-3 text-[8.4pt]">
          {settings["closing_text"] ||
            "Please do the needful as per your norms. We solicit your continious support."}
        </p>

        <div className="mt-4 text-[8.4pt]">
          <p>Thanks</p>
          <p className="mt-2">For {settings["org_heading"]},</p>
          <p className="mt-3">
            {settings["signatory_name"]} {settings["signatory_mobile"]}
          </p>
        </div>

        <div className="mt-3 text-[8.4pt]">
          <p>
            We have drawn a cheque/transfer amount to{" "}
            {app.bank_name ? <span>{app.bank_name}</span> : null}
          </p>
          <p>
            Bank Acount No:{" "}
            <Blank className="min-w-[30mm]">{app.bank_account_no ?? ""}</Blank>
            {app.bank_ifsc ? (
              <span className="ml-3">IFSC: {app.bank_ifsc}</span>
            ) : null}
          </p>
          {app.bank_account_holder ? (
            <p className="text-[7.6pt]">
              A/c Holder: {app.bank_account_holder}
            </p>
          ) : null}
        </div>

        <p className="mt-3 text-center text-[8.4pt] font-semibold underline">
          {(
            settings["print_footer"] ||
            "THIS LETTER IS VALID FOR {DAYS} DAYS SINCE ISSUED."
          ).replace("{DAYS}", String(validityDays))}
        </p>
        <p className="mt-1 text-center text-[7.2pt]">
          Valid until {shortDate(validUntil)}
        </p>
      </div>
    </div>
  );
}
